import React, { useState, useEffect, useContext } from 'react';
import { Modal, ModalHeader, ModalBody, ModalFooter, Button, Input, Table, Form, FormGroup } from 'reactstrap';
import RendicionViaticosService from '../../axios_services/rendicion_viaticos.service';
import { AppContext } from '../../application/provider';
import Loader from '../Loader/Loader';
import { Trash2, Plus, FileText, Upload, Briefcase, Paperclip, CheckCircle } from 'lucide-react';
import './ModalCrearUsuario.css';
import ModalGastoRendicion from './ModalGastoRendicion';

const ModalRendicionViatico = ({ isOpen, onClose, onSave, rendicion, periodoFiltro }) => {
    const [state] = useContext(AppContext);
    const userId = Number(state.user?.usuario_id);
    const [isLoading, setIsLoading] = useState(false);
    const [planillasDisponibles, setPlanillasDisponibles] = useState([]);
    const [detalles, setDetalles] = useState([]);
    const [gastoModalOpen, setGastoModalOpen] = useState(false);
    const [currentTipoGasto, setCurrentTipoGasto] = useState(1);
    const [currentTituloGasto, setCurrentTituloGasto] = useState("");
    const [archivosSeleccionados, setArchivosSeleccionados] = useState([]);
    const [archivosExistentes, setArchivosExistentes] = useState([]);

    const [formData, setFormData] = useState({
        planilla_id: '',
        usuario_id: userId,
        fecha_registro: new Date().toISOString().split('T')[0],
        detalle_ciudad: '',
        detalle_motivo: '',
        dia_inicio: '',
        dia_fin: '',
        monto_recibido: 0,
        monto_rendido: 0,
        monto_devolucion: 0,
    });

    useEffect(() => {
        if (isOpen) {
            if (rendicion) {
                if (rendicion.detalles) {
                    setDetalles(rendicion.detalles);
                }
                setFormData({
                    planilla_id: rendicion.planilla_id,
                    usuario_id: rendicion.usuario_id,
                    fecha_registro: rendicion.fecha_registro,
                    detalle_ciudad: rendicion.detalle_ciudad,
                    detalle_motivo: rendicion.detalle_motivo,
                    dia_inicio: rendicion.dia_inicio,
                    dia_fin: rendicion.dia_fin,
                    monto_recibido: Number(rendicion.monto_recibido) || 0,
                    monto_rendido: Number(rendicion.monto_rendido) || 0,
                    monto_devolucion: Number(rendicion.monto_devolucion) || 0,
                });
                setArchivosSeleccionados([]);
                if (rendicion.rendicion_id) {
                    fetchArchivosExistentes(rendicion.rendicion_id);
                } else {
                    setArchivosExistentes([]);
                }
            } else {
                setFormData({
                    planilla_id: '',
                    usuario_id: userId,
                    fecha_registro: new Date().toISOString().split('T')[0],
                    detalle_ciudad: '',
                    detalle_motivo: '',
                    dia_inicio: '',
                    dia_fin: '',
                    monto_recibido: 0,
                    monto_rendido: 0,
                    monto_devolucion: 0,
                });
                setDetalles([]);
                setArchivosSeleccionados([]);
                setArchivosExistentes([]);
                fetchPlanillas();
            }
        }
    }, [isOpen, rendicion]);

    const fetchArchivosExistentes = async (id) => {
        try {
            const response = await RendicionViaticosService.getArchivosAdjuntos(id);
            setArchivosExistentes(response.data || []);
        } catch (error) {
            console.error('Error fetching archivos:', error);
            setArchivosExistentes([]);
        }
    };

    const handleDeleteArchivoExistente = async (archivoId) => {
        if (!window.confirm('¿Está seguro de eliminar este documento sustentatorio?')) return;
        try {
            await RendicionViaticosService.eliminarArchivoAdjunto(archivoId);
            setArchivosExistentes(prev => prev.filter(a => a.archivo_id !== archivoId));
        } catch (error) {
            console.error('Error eliminando archivo:', error);
            alert('Error al eliminar el archivo');
        }
    };

    useEffect(() => {
        if (isOpen) {
            const totalRendido = detalles.reduce((sum, det) => sum + (Number(det.monto_pago) || 0), 0);
            const recibido = Number(formData.monto_recibido) || 0;
            const devolucion = recibido - totalRendido;

            setFormData(prev => ({
                ...prev,
                monto_rendido: totalRendido,
                monto_devolucion: devolucion
            }));
        }
    }, [detalles, formData.monto_recibido, isOpen]);

    const fetchPlanillas = async () => {
        setIsLoading(true);
        try {
            const data = await RendicionViaticosService.getPlanillasDisponibles(userId);
            setPlanillasDisponibles(data || []);
        } catch (error) {
            console.error('Error fetching planillas', error);
        } finally {
            setIsLoading(false);
        }
    };


    const handlePlanillaChange = (e) => {
        const pId = e.target.value;
        const selected = planillasDisponibles.find(p => String(p.planilla_id) === String(pId));

        if (selected) {
            const totalRecibido = (Number(selected.monto_pasaje) || 0) +
                (Number(selected.monto_hospedaje) || 0) +
                (Number(selected.monto_movilidad) || 0) +
                (Number(selected.monto_alimentacion) || 0) +
                (Number(selected.monto_adicional) || 0);

            setFormData(prev => ({
                ...prev,
                planilla_id: selected.planilla_id,
                detalle_ciudad: selected.lugar || '',
                detalle_motivo: selected.motivo || '',
                dia_inicio: selected.dia_inicio || '',
                dia_fin: selected.dia_fin || '',
                monto_recibido: totalRecibido
            }));
        } else {
            setFormData(prev => ({
                ...prev,
                planilla_id: '',
                detalle_ciudad: '',
                detalle_motivo: '',
                dia_inicio: '',
                dia_fin: '',
                monto_recibido: 0
            }));
        }
    };

    const handleAddDetalleClick = (tipoGasto, titulo) => {
        setCurrentTipoGasto(tipoGasto);
        setCurrentTituloGasto(titulo);
        setGastoModalOpen(true);
    };

    const handleSaveNuevoGasto = (nuevoGasto) => {
        setDetalles([...detalles, nuevoGasto]);
        setGastoModalOpen(false);
    };

    const handleRemoveDetalle = (index) => {
        const newDetalles = [...detalles];
        newDetalles.splice(index, 1);
        setDetalles(newDetalles);
    };

    const handleFileChange = (e) => {
        if (e.target.files && e.target.files.length > 0) {
            const nuevosArchivos = Array.from(e.target.files);
            setArchivosSeleccionados([...archivosSeleccionados, ...nuevosArchivos]);
            e.target.value = null;
        }
    };

    const handleRemoveFile = (index) => {
        setArchivosSeleccionados(archivosSeleccionados.filter((_, idx) => idx !== index));
    };

    const handleSubmit = () => {
        if (!formData.planilla_id && !rendicion) {
            alert('Debe seleccionar una planilla.');
            return;
        }

        const dataToSave = {
            ...formData,
            detalles: detalles
        };

        onSave(dataToSave, archivosSeleccionados);
    };

    const formatoSoles = (monto) => {
        return new Intl.NumberFormat('es-PE', {
            style: 'currency',
            currency: 'PEN',
        }).format(monto || 0);
    };

    const renderDetallesTable = (tipoGasto, titulo) => {
        const rows = detalles.map((det, index) => ({ ...det, originalIndex: index })).filter(d => Number(d.tipo_gasto) === tipoGasto);
        const hasComprobantes = tipoGasto !== 4;

        return (
            <div className="mb-4">
                <div className="flex justify-between items-center mb-2">
                    <h6 className="font-bold text-primary text-sm m-0">{titulo}</h6>
                    <Button size="sm" color="dark" onClick={() => handleAddDetalleClick(tipoGasto, titulo)}>+ Agregar</Button>
                </div>
                <div className="table-container shadow-sm overflow-x-auto">
                    <Table bordered size="sm" className="text-xs m-0">
                        <thead className="bg-[#14233a] text-white text-center align-middle">
                            <tr>
                                <th>Fecha</th>
                                <th>Lugar</th>
                                <th>Detalle</th>
                                {hasComprobantes && <th>Proveedor</th>}
                                {hasComprobantes && <th>T. Comprobante</th>}
                                {hasComprobantes && <th>Serie</th>}
                                {hasComprobantes && <th>Número</th>}
                                <th>Monto</th>
                                <th>Acciones</th>
                            </tr>
                        </thead>
                        <tbody>
                            {rows.length > 0 ? (
                                rows.map((det, idx) => (
                                    <tr key={idx} className="align-middle text-center">
                                        <td>{det.fecha_gasto}</td>
                                        <td>{det.lugar_gasto}</td>
                                        <td>{det.detalle_gasto}</td>

                                        {hasComprobantes && (
                                            <>
                                                <td>{det.proveedor}</td>
                                                <td>{det.tipo_comprobante === "1" || det.tipo_comprobante === 1 ? 'BOLETA' : (det.tipo_comprobante === "2" || det.tipo_comprobante === 2 ? 'FACTURA' : det.tipo_comprobante)}</td>
                                                <td>{det.serie_comprobante}</td>
                                                <td>{det.numero_comprobante}</td>
                                            </>
                                        )}
                                        <td className="text-right">{formatoSoles(det.monto_pago)}</td>
                                        <td className="text-center">
                                            <Button color="danger" size="sm" onClick={() => handleRemoveDetalle(det.originalIndex)} className="p-1">
                                                <Trash2 size={14} />
                                            </Button>
                                        </td>
                                    </tr>
                                ))
                            ) : (
                                <tr>
                                    <td colSpan={hasComprobantes ? 9 : 5} className="text-center text-muted">No hay gastos registrados</td>
                                </tr>
                            )}
                        </tbody>
                    </Table>
                </div>
            </div>
        );
    };

    return (
        <Modal isOpen={isOpen} toggle={onClose} style={{ maxWidth: '900px' }} backdrop="static">
            <ModalHeader className="modal-header">
                <div className="titulo">
                    <h1>{rendicion ? 'EDITAR RENDICIÓN DE VIÁTICOS' : 'NUEVA RENDICIÓN DE VIÁTICOS'}</h1>
                    <Button className="close-btn" onClick={onClose}>X</Button>
                </div>
            </ModalHeader>
            <div className="modal-body-footer">
                <ModalBody className="modal-body" style={{ overflowY: 'auto', padding: '1.5rem 2rem' }}>
                    {isLoading && <Loader />}

                        <fieldset className="fieldset-container">
                            <legend className="fieldset-legend text-sm font-bold" style={{ fontSize: '0.95rem' }}>
                                <FileText size={16} style={{ marginRight: '0.5rem' }} />
                                Datos generales
                            </legend>

                            {!rendicion && (
                                <FormGroup className="form-group">
                                    <label>Asunto de Viático:</label>
                                    <Input type="select" className="form-control small-input" value={formData.planilla_id} onChange={handlePlanillaChange}>
                                        <option value="">-- Seleccionar Asunto --</option>
                                        {planillasDisponibles
                                            .filter(p => !periodoFiltro || (p.dia_fin && p.dia_fin.startsWith(periodoFiltro)))
                                            .map(p => (
                                            <option key={p.planilla_id} value={p.planilla_id}>
                                                {p.asunto ? p.asunto.toUpperCase() : (p.lugar ? p.lugar.toUpperCase() : `PLANILLA ${p.planilla_id}`)}
                                            </option>
                                        ))}
                                    </Input>
                                </FormGroup>
                            )}
                            
                            <FormGroup className="form-group">
                                <label>Fecha de Registro:</label>
                                <Input type="date" className="form-control small-input" value={formData.fecha_registro} disabled />
                            </FormGroup>
                            <FormGroup className="form-group">
                                <label>Ciudad Destino:</label>
                                <Input type="text" className="form-control small-input" value={formData.detalle_ciudad} onChange={e => setFormData({ ...formData, detalle_ciudad: e.target.value })} disabled={!rendicion} />
                            </FormGroup>
                            <FormGroup className="form-group">
                                <label>Día Inicio:</label>
                                <Input type="date" className="form-control small-input" value={formData.dia_inicio} onChange={e => setFormData({ ...formData, dia_inicio: e.target.value })} disabled={!rendicion} />
                            </FormGroup>
                            <FormGroup className="form-group">
                                <label>Día Fin:</label>
                                <Input type="date" className="form-control small-input" value={formData.dia_fin} onChange={e => setFormData({ ...formData, dia_fin: e.target.value })} disabled={!rendicion} />
                            </FormGroup>
                            <FormGroup className="form-group">
                                <label>Motivo:</label>
                                <Input type="text" className="form-control small-input" value={formData.detalle_motivo} onChange={e => setFormData({ ...formData, detalle_motivo: e.target.value })} disabled={!rendicion} />
                            </FormGroup>
                        </fieldset>

                    <fieldset className="fieldset-container">
                        <legend className="fieldset-legend text-sm font-bold" style={{ fontSize: '0.95rem' }}>
                            <Briefcase size={16} style={{ marginRight: '0.5rem' }} />
                            Detalle de gastos
                        </legend>
                        {renderDetallesTable(1, "Gastos de movilidad")}
                        {renderDetallesTable(2, "Gastos de alimentación")}
                        {renderDetallesTable(3, "Gastos de hospedaje")}
                        {renderDetallesTable(4, "Gastos sin sustento")}
                    </fieldset>

                    <fieldset className="fieldset-container">
                        <legend className="fieldset-legend text-sm font-bold" style={{ fontSize: '0.95rem' }}>
                            <Paperclip size={16} style={{ marginRight: '0.5rem' }} />
                            Documentos sustentatorios
                        </legend>
                        <div style={{ display: 'grid', gridTemplateColumns: '1fr', gap: '0.75rem' }}>
                            <FormGroup className="form-group">
                                <label>Adjuntar Documentos:</label>
                                <div style={{ flex: 1, display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                                    <input type="file" id="real-file-input" style={{ display: 'none' }} multiple accept=".pdf,.jpg,.jpeg,.png" onChange={handleFileChange} />
                                    <button type="button" style={{ backgroundColor: '#e0e0e0', border: '1px solid #ccc', borderRadius: '4px', padding: '2px 8px', fontSize: '0.7rem', cursor: 'pointer', color: '#333' }} onClick={() => document.getElementById('real-file-input').click()}>
                                        Elegir archivos
                                    </button>
                                    <span style={{ fontSize: '0.7rem', color: '#666', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                        {archivosSeleccionados.length > 0 ? `${archivosSeleccionados.length} archivo(s) seleccionado(s)` : 'Ningún archivo seleccionado'}
                                    </span>
                                </div>
                            </FormGroup>

                            {archivosExistentes && archivosExistentes.length > 0 && (
                                <div className="mb-2">
                                    <div className="text-xs font-semibold text-gray-600 mb-1">Comprobantes:</div>
                                    <div className="flex flex-wrap gap-2">
                                        {archivosExistentes.map((archivo, index) => (
                                            <div key={archivo.archivo_id} className="flex items-center justify-between gap-2 bg-gray-50 border rounded px-3 py-1 text-xs shadow-sm">
                                                <div className="flex items-center gap-1 cursor-pointer" onClick={() => window.open(archivo.url_completa, '_blank')} title="Abrir comprobante">
                                                    <FileText size={14} className="text-primary" />
                                                    <span className="text-muted font-medium hover:text-blue-600">Comprobante {index + 1}</span>
                                                </div>
                                                <Button
                                                    color="link"
                                                    className="text-danger p-0 m-0 line-height-1"
                                                    onClick={() => handleDeleteArchivoExistente(archivo.archivo_id)}
                                                    title="Eliminar"
                                                >
                                                    <Trash2 size={14} />
                                                </Button>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            )}

                            {archivosSeleccionados.length > 0 && (
                                <div className="flex flex-wrap gap-2 mt-2">
                                    {archivosSeleccionados.map((file, index) => (
                                        <div key={index} className="flex items-center justify-between gap-2 bg-white border rounded px-3 py-1 text-xs shadow-sm">
                                            <span className="text-muted truncate max-w-xs">{file.name}</span>
                                            <Button
                                                color="link"
                                                className="text-danger p-0 m-0 line-height-1"
                                                onClick={() => handleRemoveFile(index)}
                                            >
                                                ✕
                                            </Button>
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>
                    </fieldset>

                    <div className="flex justify-end">
                        <div className="w-1/2 table-container shadow-sm overflow-x-auto">
                            <Table bordered size="sm" className="text-xs m-0">
                                <thead className="bg-[#14233a] text-white text-center">
                                    <tr>
                                        <th colSpan="2">Resumen final</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    <tr>
                                        <td className="font-bold">Monto recibido</td>
                                        <td className="text-right font-bold text-primary">{formatoSoles(formData.monto_recibido)}</td>
                                    </tr>
                                    <tr>
                                        <td className="font-bold">Monto rendido</td>
                                        <td className="text-right font-bold text-success">{formatoSoles(formData.monto_rendido)}</td>
                                    </tr>
                                    <tr>
                                        <td className="font-bold">Devolución</td>
                                        <td className="text-right font-bold text-danger">{formatoSoles(formData.monto_devolucion)}</td>
                                    </tr>
                                </tbody>
                            </Table>
                        </div>
                    </div>

                </ModalBody>
                <ModalFooter style={{ padding: '1rem 2rem' }}>
                    <div className="button-container flex justify-end w-100">
                        <Button color="primary" className="button-custom" onClick={handleSubmit} disabled={isLoading || (!formData.planilla_id && !rendicion)}>
                            {isLoading ? 'Guardando...' : 'Guardar Rendición'}
                        </Button>
                    </div>
                </ModalFooter>
            </div>

            <ModalGastoRendicion
                isOpen={gastoModalOpen}
                onClose={() => setGastoModalOpen(false)}
                onSave={handleSaveNuevoGasto}
                tipoGasto={currentTipoGasto}
                titulo={currentTituloGasto}
            />
        </Modal>
    );
};

export default ModalRendicionViatico;